import { useEffect, useRef } from "react";

/**
 * Realistic steam, in ~2KB of runtime and zero dependencies.
 *
 * Canvas 2D, not WebGL and not a video: the brief asks for lightweight and
 * web-native, and 2D is enough because the heavy lifting happens outside the
 * loop —
 *
 *   • each particle is ONE pre-rendered radial-gradient sprite, drawn with
 *     per-particle alpha/scale/rotation, never a per-frame gradient;
 *   • the canvas element itself carries a CSS blur, which the compositor runs
 *     on the GPU. Forty soft blobs under a 12px blur read as continuous
 *     volumetric vapour; the same forty without it read as bubbles.
 *
 * Motion is buoyancy plus two incommensurate sine sways per particle — cheap
 * curl-noise. Real steam doesn't rise straight and doesn't oscillate on one
 * frequency; two layered sines with per-particle phase is the smallest thing
 * that looks alive.
 *
 * The stage (a mode string) only changes SPAWN parameters. Existing particles
 * always finish their own lives, so a stage change never causes a visible cut
 * — the steam of phase 1 drifts naturally into the gathering of phase 2.
 */

export type SteamMode =
  /** Rising gently from the iron's toe and vents. */
  | "iron"
  /** Denser, spawn drifting toward centre — the transition veil. */
  | "gather"
  /** Centred column behind the mark reveal. */
  | "reveal"
  /** A faint idle wisp behind the settled logo. */
  | "idle"
  /** Stop spawning; loop exits once the last particle dies. */
  | "off";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  size: number;
  growth: number;
  rot: number;
  vrot: number;
  peak: number;
  sway1: number;
  sway2: number;
  phase1: number;
  phase2: number;
}

interface SpawnProfile {
  /** Particles per second. */
  rate: number;
  /**
   * Spawn from the IRON's measured position rather than from `sources`.
   *
   * The iron is placed in `dvh` units at a width of `min(88vw, 560px)`, so its
   * on-screen position moves with the viewport. Fixed fractions cannot track
   * that — measured at 375x812 they put the plume 176px above the vents, which
   * reads as steam hanging in the air rather than leaving the iron.
   */
  anchorToIron?: true;
  /** Spawn centres as fractions of canvas size: [x, y, jitterX, jitterY][]. */
  sources: ReadonlyArray<readonly [number, number, number, number]>;
  /** Upward velocity range, canvas-heights per second. */
  rise: readonly [number, number];
  /** Peak opacity range. */
  peak: readonly [number, number];
  /** Sprite radius range as a fraction of canvas height. */
  size: readonly [number, number];
  life: readonly [number, number];
  /** Horizontal pull toward centre, for the gathering phase. */
  centrePull: number;
}

const PROFILES: Record<Exclude<SteamMode, "off">, SpawnProfile> = {
  iron: {
    rate: 16,
    anchorToIron: true,
    // Fallback only, for the case where the iron is not mounted.
    sources: [
      [0.38, 0.66, 0.05, 0.015],
      [0.55, 0.62, 0.04, 0.015],
    ],
    rise: [0.1, 0.17],
    peak: [0.05, 0.11],
    size: [0.05, 0.1],
    life: [2.6, 4.2],
    centrePull: 0,
  },
  gather: {
    rate: 34,
    sources: [
      [0.42, 0.62, 0.1, 0.03],
      [0.5, 0.7, 0.14, 0.04],
      [0.58, 0.6, 0.1, 0.03],
    ],
    rise: [0.13, 0.22],
    peak: [0.09, 0.16],
    size: [0.07, 0.14],
    life: [2.4, 3.8],
    centrePull: 0.045,
  },
  reveal: {
    rate: 22,
    sources: [
      [0.5, 0.62, 0.1, 0.05],
      [0.5, 0.5, 0.13, 0.05],
    ],
    rise: [0.1, 0.16],
    peak: [0.07, 0.13],
    size: [0.08, 0.16],
    life: [2.8, 4.4],
    centrePull: 0.02,
  },
  idle: {
    rate: 3.5,
    sources: [[0.5, 0.42, 0.09, 0.04]],
    rise: [0.05, 0.09],
    peak: [0.03, 0.055],
    size: [0.06, 0.12],
    life: [3.5, 5.5],
    centrePull: 0,
  },
};

const MAX_PARTICLES = 110;
/** Blur hides resolution, so the buffer can be genuinely small. */
const DPR_CAP = 1.25;

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** One soft warm-white blob, rendered once and stamped thereafter. */
function makeSprite(): HTMLCanvasElement {
  const sprite = document.createElement("canvas");
  sprite.width = 128;
  sprite.height = 128;
  const ctx = sprite.getContext("2d")!;
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  // Slightly warm, never pure white — matches the concept's lamp-lit vapour.
  g.addColorStop(0, "rgba(236, 226, 210, 0.55)");
  g.addColorStop(0.35, "rgba(228, 216, 198, 0.28)");
  g.addColorStop(0.7, "rgba(215, 202, 184, 0.09)");
  g.addColorStop(1, "rgba(215, 202, 184, 0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  return sprite;
}

export function SteamCanvas({
  mode,
  className = "",
  ironRef,
}: {
  mode: SteamMode;
  className?: string;
  /**
   * The iron's wrapper, so the plume can start at its vents.
   *
   * Read via `offsetTop`/`offsetLeft`, which are LAYOUT values: the shared
   * parent carries the RTL mirror transform, and the canvas paints in that
   * same untransformed space, so layout offsets line up while
   * `getBoundingClientRect` (post-transform) would not.
   */
  ironRef?: React.RefObject<HTMLElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const modeRef = useRef<SteamMode>(mode);
  modeRef.current = mode;
  const ironElRef = useRef<React.RefObject<HTMLElement | null> | undefined>(ironRef);
  ironElRef.current = ironRef;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // The React side already skips the whole entrance under reduced motion;
    // this is defence in depth so the loop cannot run even if a future caller
    // mounts the canvas directly.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const sprite = makeSprite();
    const particles: Particle[] = [];
    let raf = 0;
    let last = performance.now();
    let spawnDebt = 0;
    let running = true;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      const { clientWidth, clientHeight } = canvas;
      canvas.width = Math.max(1, Math.round(clientWidth * dpr));
      canvas.height = Math.max(1, Math.round(clientHeight * dpr));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    /**
     * The iron's steam vents, in canvas buffer pixels.
     *
     * Measured on demand — only while the iron profile is spawning, roughly
     * a dozen times over that phase, so the forced layout is negligible and
     * the origin is always current even as the viewport changes.
     */
    const ventOrigin = (): { x: number; y: number; spread: number } | null => {
      const el = ironElRef.current?.current;
      if (!el || !el.offsetWidth) return null;
      const dpr = canvas.width / Math.max(1, canvas.clientWidth);
      // Vent positions within the iron's 560x300 viewBox.
      return {
        x: (el.offsetLeft + (165 / 560) * el.offsetWidth) * dpr,
        y: (el.offsetTop + (208 / 300) * el.offsetHeight) * dpr,
        spread: (44 / 560) * el.offsetWidth * dpr,
      };
    };

    const spawn = (profile: SpawnProfile) => {
      if (particles.length >= MAX_PARTICLES) return;
      const w = canvas.width;
      const h = canvas.height;
      const src = profile.sources[Math.floor(Math.random() * profile.sources.length)]!;
      const size = rand(profile.size[0], profile.size[1]) * h;

      const vent = profile.anchorToIron ? ventOrigin() : null;
      const spawnX = vent ? vent.x + rand(-vent.spread, vent.spread) : (src[0] + rand(-src[2], src[2])) * w;
      const spawnY = vent ? vent.y + rand(-0.008, 0.008) * h : (src[1] + rand(-src[3], src[3])) * h;

      particles.push({
        x: spawnX,
        y: spawnY,
        vx: rand(-0.01, 0.01) * h,
        vy: -rand(profile.rise[0], profile.rise[1]) * h,
        age: 0,
        life: rand(profile.life[0], profile.life[1]),
        size,
        growth: rand(0.16, 0.3),
        rot: rand(0, Math.PI * 2),
        vrot: rand(-0.25, 0.25),
        peak: rand(profile.peak[0], profile.peak[1]),
        sway1: rand(0.008, 0.02) * h,
        sway2: rand(0.004, 0.012) * h,
        phase1: rand(0, Math.PI * 2),
        phase2: rand(0, Math.PI * 2),
      });
    };

    const frame = (now: number) => {
      if (!running) return;
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      const m = modeRef.current;
      const w = canvas.width;
      const h = canvas.height;

      if (m !== "off") {
        const profile = PROFILES[m];
        spawnDebt += profile.rate * dt;
        while (spawnDebt >= 1) {
          spawnDebt -= 1;
          spawn(profile);
        }
      }

      ctx.clearRect(0, 0, w, h);

      const centrePull = m !== "off" ? PROFILES[m].centrePull : 0;
      const t = now / 1000;

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]!;
        p.age += dt;
        if (p.age >= p.life) {
          particles.splice(i, 1);
          continue;
        }

        // Two incommensurate sways plus buoyancy; the centre pull is what
        // makes the gathering phase visibly converge.
        p.x +=
          (p.vx +
            Math.sin(t * 0.9 + p.phase1) * p.sway1 +
            Math.sin(t * 2.3 + p.phase2) * p.sway2 +
            (w / 2 - p.x) * centrePull) *
          dt;
        p.y += p.vy * dt;
        p.vy *= 1 - 0.06 * dt; // old steam slows as it disperses
        p.rot += p.vrot * dt;

        const lifeFrac = p.age / p.life;
        // Smooth in over the first quarter, out over the final half.
        const fadeIn = Math.min(1, lifeFrac / 0.25);
        const fadeOut = lifeFrac > 0.5 ? 1 - (lifeFrac - 0.5) / 0.5 : 1;
        const alpha = p.peak * fadeIn * fadeOut * fadeOut;
        if (alpha <= 0.002) continue;

        const scale = 1 + p.growth * p.age;
        const r = p.size * scale;

        ctx.globalAlpha = alpha;
        ctx.setTransform(1, 0, 0, 1, p.x, p.y);
        ctx.rotate(p.rot);
        // Slightly elongated stamp — round steam reads as bubbles.
        ctx.drawImage(sprite, -r, -r * 1.25, r * 2, r * 2.5);
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;

      if (modeRef.current === "off" && particles.length === 0) return; // loop ends
      raf = requestAnimationFrame(frame);
    };

    // Steam nobody can see is battery drain: stop cleanly when the tab hides,
    // resume with a fresh clock when it returns (a stale `last` would make
    // every particle jump a whole hidden-duration step).
    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else {
        // Cancel before scheduling. A canvas MOUNTED in a hidden tab has a
        // pending callback that never ran — hidden documents get no rendering
        // opportunities, but the callback is not discarded. Scheduling on top
        // of it would leave two live loops racing, each clearing and redrawing
        // the other's frame at double the particle cost.
        cancelAnimationFrame(raf);
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      // The compositor-level blur that turns particles into vapour.
      style={{ filter: "blur(12px)" }}
    />
  );
}
